import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EmployeeTable from './components/EmployeeTable';
import EmployeeFilters from './components/EmployeeFilters';
import BulkActionsBar from './components/BulkActionsBar';
import EmployeeModal from './components/EmployeeModal';
import PhotoUploadModal from './components/PhotoUploadModal';
import { downloadArbeitszeitOdt, downloadArbeitszeitOfficial } from '../../utils/odtGenerator';
import { useSupabase } from '../../hooks/useSupabase';
import { useAuth } from '../../contexts/AuthContext';
import { useColumnVisibility } from '../../contexts/ColumnVisibilityContext';

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { supabase } = useSupabase();
  const { user } = useAuth();
  const { isColumnsHidden, toggleColumnsVisibility } = useColumnVisibility();
  
  // State management
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasNoRecordsForMonth, setHasNoRecordsForMonth] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedHireType, setSelectedHireType] = useState('');
  const [minSalary, setMinSalary] = useState('');
  const [maxSalary, setMaxSalary] = useState('');
  const [minGehaltS, setMinGehaltS] = useState('');
  const [maxGehaltS, setMaxGehaltS] = useState('');
  const [minTagesrate, setMinTagesrate] = useState('');
  const [maxTagesrate, setMaxTagesrate] = useState('');
  
  // Month filter state - default to current month
  const getCurrentMonthYear = () => {
    const now = new Date();
    return { month: now.getMonth(), year: now.getFullYear() };
  };
  const [selectedMonthYear, setSelectedMonthYear] = useState(getCurrentMonthYear());
  
  // Modal states
  const [employeeModal, setEmployeeModal] = useState({
    isOpen: false,
    employee: null,
    mode: 'view' // 'view', 'edit', 'create'
  });
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    employee: null
  });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });

  // Add mode switch handler for Header component
  const handleModeSwitch = () => {
    // Add your mode switch logic here if needed
    console.log('Mode switch triggered');
  };

  // Load employees from database
  const loadEmployees = async (monthYear = null) => {
    try {
      setIsLoading(true);
      setError(null);
      setHasNoRecordsForMonth(false);
      
      const { data, error } = await supabase
        .from('employees')
        .select(`
          *,
          photo,
          user_profile:user_profiles(*)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Use provided month/year or selected month/year, default to current month
      const monthYearToUse = monthYear || selectedMonthYear;
      const filterMonth = monthYearToUse.month;
      const filterYear = monthYearToUse.year;
      
      // Calculate start and end of selected month
      const startOfMonth = new Date(filterYear, filterMonth, 1);
      const endOfMonth = new Date(filterYear, filterMonth + 1, 0, 23, 59, 59, 999);
      
      // Load time entries for current month only to calculate monthly Gehalt S for each employee
      const { data: timeEntriesData, error: timeEntriesError } = await supabase
        .from('time_entries')
        .select(`
          employee_id, 
          dauer_t,
          clock_in_time,
          clock_out_time,
          employees!inner(tagesrate)
        `)
        .not('dauer_t', 'is', null)
        .gte('clock_in_time', startOfMonth.toISOString())
        .lte('clock_in_time', endOfMonth.toISOString());

      if (timeEntriesError) {
        console.warn('Could not load time entries for Gehalt S calculation:', timeEntriesError);
      }

      // Check if there are any time entries for the selected month/year
      const hasRecords = timeEntriesData && timeEntriesData.length > 0;
      setHasNoRecordsForMonth(!hasRecords);

      // Debug: Log the raw time entries data for selected month
      console.log('Selected month time entries data:', timeEntriesData);
      console.log('Selected month:', filterMonth + 1, 'Year:', filterYear);
      console.log('Date range:', startOfMonth.toISOString(), 'to', endOfMonth.toISOString());
      
      // Debug: Check if there are any time entries for Vollzeit employees in selected month
      const { data: allTimeEntries, error: allTimeEntriesError } = await supabase
        .from('time_entries')
        .select('employee_id, employees!inner(hire_type, name)')
        .eq('employees.hire_type', 'Vollzeit')
        .gte('clock_in_time', startOfMonth.toISOString())
        .lte('clock_in_time', endOfMonth.toISOString());
      
      console.log('Selected month time entries for Vollzeit employees:', allTimeEntries);
      
      // Debug: Check all time entries with employee names for selected month
      const { data: allTimeEntriesWithNames, error: allTimeEntriesWithNamesError } = await supabase
        .from('time_entries')
        .select('employee_id, dauer_t, employees!inner(name, hire_type, tagesrate)')
        .gte('clock_in_time', startOfMonth.toISOString())
        .lte('clock_in_time', endOfMonth.toISOString());
      
      console.log('Selected month time entries with employee names:', allTimeEntriesWithNames);

      // Calculate monthly Gehalt S per employee (sum of calculated Gehalt S from current month time entries only)
      const monthlyGehaltSByEmployee = {};
      if (timeEntriesData) {
        timeEntriesData.forEach(entry => {
          if (!monthlyGehaltSByEmployee[entry.employee_id]) {
            monthlyGehaltSByEmployee[entry.employee_id] = 0;
          }
          
          // Calculate dauer_t from clock times if it's 0 or missing
          let dauerT = entry.dauer_t || 0;
          if (dauerT === 0 && entry.clock_in_time && entry.clock_out_time) {
            const start = new Date(entry.clock_in_time);
            const end = new Date(entry.clock_out_time);
            const diff = end - start;
            if (diff > 0) {
              dauerT = diff / (1000 * 60 * 60); // Convert to hours
            }
          }
          
          // Calculate Gehalt S for this time entry: (Dauer T - dynamic pause) / 8 × Tagesrate
          const tagesrate = entry.employees?.tagesrate || 0;
          // Calculate dynamic pause: 15 minutes for every 2 hours worked
          const pauseHours = Math.floor(dauerT / 2) * 0.25; // 15 minutes = 0.25 hours
          const effectiveHours = Math.max(0, dauerT - pauseHours);
          const gehaltSValue = (effectiveHours / 8) * tagesrate;
          
          // Debug: Log each calculation
          console.log(`Employee ${entry.employee_id}: dauerT=${dauerT}, tagesrate=${tagesrate}, gehaltSValue=${gehaltSValue}`);
          
          monthlyGehaltSByEmployee[entry.employee_id] += gehaltSValue;
        });
        
        // Debug: Log the calculated monthly totals
        console.log('Monthly Gehalt S totals by employee:', monthlyGehaltSByEmployee);
      } else {
        console.log('No time entries data found');
      }

      // Transform data to match component expectations
      const transformedEmployees = data?.map(emp => {
        const monthlyGehaltS = monthlyGehaltSByEmployee[emp.id] || 0;
        
        // Debug: Log each employee's data
        console.log(`Employee ${emp.id} (${emp.name}): hireType=${emp.hire_type}, tagesrate=${emp.tagesrate}, monthlyGehaltS=${monthlyGehaltS}`);
        
        return {
          id: emp.id,
          name: emp.name,
          email: emp.email,
          employeeCode: emp.employee_code,
          position: emp.position,
          hireDate: emp.hire_date,
          hireType: emp.hire_type,
          salary: emp.salary,
          gehaltS: emp.gehalt_s,
          tagesrate: emp.tagesrate,
          photo: emp.photo,
          dauerS: 0, // Not used in new calculation
          monthlyGehaltS: monthlyGehaltS, // Monthly Gehalt S from current month time entries only
          totalGehaltS: monthlyGehaltS, // Monthly Gehalt S from current month time entries only
          status: emp.is_active ? 'active' : 'inactive',
          phone: emp.phone,
          userProfileId: emp.user_profile_id,
          userProfile: emp.user_profile,
          createdAt: emp.created_at,
          updatedAt: emp.updated_at
        };
      }) || [];

      setEmployees(transformedEmployees);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError(err.message || 'Fehler beim Laden der Mitarbeiter');
    } finally {
      setIsLoading(false);
    }
  };

  // Load employees on component mount and when month/year changes
  useEffect(() => {
    loadEmployees();
  }, [selectedMonthYear]);

  // Handle URL parameters for actions
  useEffect(() => {
    const action = searchParams?.get('action');
    if (action === 'add') {
      setEmployeeModal({
        isOpen: true,
        employee: null,
        mode: 'create'
      });
      // Clear the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Filter employees based on search and filter criteria
  const filteredAndSortedEmployees = useMemo(() => {
    let filtered = employees?.filter(employee => {
      const matchesSearch = !searchTerm || 
        employee?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        employee?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        employee?.employeeCode?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesStatus = !selectedStatus || employee?.status === selectedStatus;
      const matchesHireType = !selectedHireType || employee?.hireType === selectedHireType;
      const matchesSalary = (!minSalary || employee?.salary >= minSalary) && (!maxSalary || employee?.salary <= maxSalary);
      const matchesGehaltS = (!minGehaltS || employee?.gehaltS >= minGehaltS) && (!maxGehaltS || employee?.gehaltS <= maxGehaltS);
      const matchesTagesrate = (!minTagesrate || employee?.tagesrate >= minTagesrate) && (!maxTagesrate || employee?.tagesrate <= maxTagesrate);
      
      return matchesSearch && matchesStatus && matchesHireType && matchesSalary && matchesGehaltS && matchesTagesrate;
    });

    setFilteredEmployees(filtered);
    return filtered;
  }, [employees, searchTerm, selectedStatus, selectedHireType, minSalary, maxSalary, minGehaltS, maxGehaltS, minTagesrate, maxTagesrate]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    setEmployeeModal({
      isOpen: true,
      employee,
      mode: 'view'
    });
  };

  // Handle employee edit
  const handleEmployeeEdit = (employee) => {
    setEmployeeModal({
      isOpen: true,
      employee,
      mode: 'edit'
    });
  };

  // Handle employee toggle (activate/deactivate)
  const handleEmployeeToggle = async (employee) => {
    const newStatus = employee?.status === 'active' ? 'inactive' : 'active';
    
    try {
      const { error } = await supabase
        .from('employees')
        .update({ is_active: newStatus === 'active' })
        .eq('id', employee?.id);

      if (error) {
        throw error;
      }
      
      setEmployees(prev => prev?.map(emp => 
        emp?.id === employee?.id 
          ? { ...emp, status: newStatus }
          : emp
      ));
    } catch (error) {
      console.error('Failed to update employee status:', error);
      setError(error.message || 'Fehler beim Aktualisieren des Mitarbeiterstatus');
    }
  };

  // Handle photo upload
  const handlePhotoUpload = (employee) => {
    setPhotoModal({
      isOpen: true,
      employee
    });
  };

  // Handle photo save
  const handlePhotoSave = async (employeeId, photoUrl) => {
    try {
      // Update the database
      const { error } = await supabase
        .from('employees')
        .update({ photo: photoUrl })
        .eq('id', employeeId);

      if (error) {
        throw error;
      }

      // Update local state
      setEmployees(prev => prev?.map(emp => 
        emp?.id === employeeId 
          ? { ...emp, photo: photoUrl }
          : emp
      ));
    } catch (error) {
      console.error('Failed to save photo:', error);
      throw error;
    }
  };

  // Handle employee save (create/edit)
  const handleEmployeeSave = async (employeeData) => {
    try {
      if (employeeModal?.mode === 'create') {
        // Create new employee
        const { data, error } = await supabase
          .from('employees')
          .insert([{
            employee_code: employeeData.employeeCode,
            name: employeeData.name,
            email: employeeData.email,
            phone: employeeData.phone,
            position: employeeData.position,
            hire_date: employeeData.hireDate,
            hire_type: employeeData.hireType,
            salary: employeeData.salary,
            gehalt_s: employeeData.gehaltS,
            tagesrate: employeeData.tagesrate,
            photo: employeeData.photo,
            is_active: employeeData.status === 'active'
          }])
          .select(`
            *,
            user_profile:user_profiles(*)
          `)
          .single();

        if (error) {
          throw error;
        }

        // Transform and add to state
        const transformedEmployee = {
          id: data.id,
          name: data.name,
          email: data.email,
          employeeCode: data.employee_code,
          position: data.position,
          hireDate: data.hire_date,
          hireType: data.hire_type,
          salary: data.salary,
          gehaltS: data.gehalt_s,
          tagesrate: data.tagesrate,
          photo: data.photo,
          dauerS: 0, // Will be calculated from time entries
          totalGehaltS: 0, // Will be calculated from time entries
          status: data.is_active ? 'active' : 'inactive',
          phone: data.phone,
          userProfileId: data.user_profile_id,
          userProfile: data.user_profile,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };

        setEmployees(prev => [transformedEmployee, ...prev]);
      } else {
        // Update existing employee
        const { data, error } = await supabase
          .from('employees')
          .update({
            employee_code: employeeData.employeeCode,
            name: employeeData.name,
            email: employeeData.email,
            phone: employeeData.phone,
            position: employeeData.position,
            hire_date: employeeData.hireDate,
            hire_type: employeeData.hireType,
            salary: employeeData.salary,
            gehalt_s: employeeData.gehaltS,
            tagesrate: employeeData.tagesrate,
            photo: employeeData.photo,
            is_active: employeeData.status === 'active'
          })
          .eq('id', employeeModal?.employee?.id)
          .select(`
            *,
            user_profile:user_profiles(*)
          `)
          .single();

        if (error) {
          throw error;
        }

        // Transform and update state
        const transformedEmployee = {
          id: data.id,
          name: data.name,
          email: data.email,
          employeeCode: data.employee_code,
          position: data.position,
          hireDate: data.hire_date,
          hireType: data.hire_type,
          salary: data.salary,
          gehaltS: data.gehalt_s,
          tagesrate: data.tagesrate,
          photo: data.photo,
          dauerS: employeeModal?.employee?.dauerS || 0, // Preserve existing total Dauer S
          totalGehaltS: employeeModal?.employee?.totalGehaltS || 0, // Preserve existing total Gehalt S
          status: data.is_active ? 'active' : 'inactive',
          phone: data.phone,
          userProfileId: data.user_profile_id,
          userProfile: data.user_profile,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };

        setEmployees(prev => prev?.map(emp => 
          emp?.id === employeeModal?.employee?.id 
            ? transformedEmployee
            : emp
        ));
      }
    } catch (error) {
      console.error('Failed to save employee:', error);
      throw error;
    }
  };

  // Handle bulk actions
  const handleBulkStatusChange = async (status) => {
    try {
      const { error } = await supabase
        .from('employees')
        .update({ is_active: status === 'active' })
        .in('id', selectedEmployees);

      if (error) {
        throw error;
      }
      
      setEmployees(prev => prev?.map(emp => 
        selectedEmployees?.includes(emp?.id) 
          ? { ...emp, status }
          : emp
      ));
      
      setSelectedEmployees([]);
    } catch (error) {
      console.error('Failed to update employee statuses:', error);
      setError(error.message || 'Fehler beim Aktualisieren der Mitarbeiterstatus');
    }
  };

  const handleBulkExport = () => {
    const selectedData = employees?.filter(emp => selectedEmployees?.includes(emp?.id));
    const csvContent = [
      ['Name', 'E-Mail', 'Mitarbeiternummer', 'Position', 'Status', 'Einstellungsdatum', 'Anstellungsart', 'Gehalt', 'Gehalt S', 'Gehalt T', 'Tagesrate']?.join(','),
      ...selectedData?.map(emp => {
        const gehaltSValue = emp?.totalGehaltS || emp?.gehaltS || 0;
        const salaryValue = emp?.salary || 0;
        const gehaltTValue = gehaltSValue - salaryValue;
        return [
          emp?.name,
          emp?.email,
          emp?.employeeCode,
          emp?.position,
          emp?.status,
          emp?.hireDate,
          emp?.hireType,
          salaryValue,
          gehaltSValue,
          gehaltTValue,
          emp?.tagesrate
        ]?.join(',')
      })
    ]?.join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mitarbeiter_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const handleDownloadArbeitszeit = async () => {
    try {
      if (!selectedEmployees || selectedEmployees.length !== 1) {
        alert('Bitte genau einen Mitarbeiter auswählen.');
        return;
      }
      const employee = employees.find(e => e.id === selectedEmployees[0]);
      if (!employee) {
        alert('Mitarbeiter nicht gefunden.');
        return;
      }
      await downloadArbeitszeitOdt(employee);
    } catch (e) {
      console.error(e);
      alert('Fehler beim Erstellen der Datei.');
    }
  };

  const handleDownloadArbeitszeitOfficial = async () => {
    try {
      if (!selectedEmployees || selectedEmployees.length !== 1) {
        alert('Bitte genau einen Mitarbeiter auswählen.');
        return;
      }
      const employee = employees.find(e => e.id === selectedEmployees[0]);
      if (!employee) {
        alert('Mitarbeiter nicht gefunden.');
        return;
      }
      await downloadArbeitszeitOfficial(employee);
    } catch (e) {
      console.error(e);
      alert('Fehler beim Herunterladen der offiziellen Datei.');
    }
  };

  // Handle individual employee delete
  const handleEmployeeDelete = async (employee) => {
    if (window.confirm(`Sind Sie sicher, dass Sie den Mitarbeiter "${employee?.name}" löschen möchten?`)) {
      try {
        const { error } = await supabase
          .from('employees')
          .delete()
          .eq('id', employee?.id);

        if (error) {
          throw error;
        }
        
        setEmployees(prev => prev?.filter(emp => emp?.id !== employee?.id));
        
        // Remove from selected employees if it was selected
        if (selectedEmployees?.includes(employee?.id)) {
          setSelectedEmployees(prev => prev?.filter(id => id !== employee?.id));
        }
      } catch (error) {
        console.error('Failed to delete employee:', error);
        setError(error.message || 'Fehler beim Löschen des Mitarbeiters');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Sind Sie sicher, dass Sie ${selectedEmployees?.length} Mitarbeiter löschen möchten?`)) {
      try {
        const { error } = await supabase
          .from('employees')
          .delete()
          .in('id', selectedEmployees);

        if (error) {
          throw error;
        }
        
        setEmployees(prev => prev?.filter(emp => !selectedEmployees?.includes(emp?.id)));
        setSelectedEmployees([]);
      } catch (error) {
        console.error('Failed to delete employees:', error);
        setError(error.message || 'Fehler beim Löschen der Mitarbeiter');
      }
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('');
    setSelectedHireType('');
    setMinSalary('');
    setMaxSalary('');
    setMinGehaltS('');
    setMaxGehaltS('');
    setMinTagesrate('');
    setMaxTagesrate('');
    setSelectedMonthYear(getCurrentMonthYear());
  };
  
  // Handle month/year change
  const handleMonthYearChange = (month, year) => {
    setSelectedMonthYear({ month, year });
  };
  
  // Handle "Last Month" quick filter
  const handleLastMonthFilter = () => {
    const now = new Date();
    const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    setSelectedMonthYear({ month: lastMonth, year: lastMonthYear });
  };

  // Notification counts for header
  const notificationCounts = {
    orders: 0,
    technicians: 0
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" notificationCounts={notificationCounts} />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
          notificationCounts={notificationCounts}
        />
      </div>
      <main className={`transition-all duration-200 ease-out ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      } pt-16`}>
        <div className="flex">
          <div className="flex-1 p-6 lg:pr-0">
            <div className="max-w-none">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-text-primary">Mitarbeiterverwaltung</h1>
              <p className="mt-2 text-text-secondary">
                Verwalten Sie Mitarbeiterdaten, Rollen und Personalinformationen
              </p>
            </div>
            
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={loadEmployees}
                iconName="RefreshCw"
                iconPosition="left"
                className="touch-feedback"
                disabled={isLoading}
              >
                Aktualisieren
              </Button>
              
              <Button
                variant="default"
                onClick={() => setEmployeeModal({
                  isOpen: true,
                  employee: null,
                  mode: 'create'
                })}
                iconName="Plus"
                iconPosition="left"
                className="touch-feedback"
              >
                Mitarbeiter hinzufügen
              </Button>
            </div>
          </div>

          {/* Filters */}
          <EmployeeFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            selectedHireType={selectedHireType}
            onHireTypeChange={setSelectedHireType}
            minSalary={minSalary}
            onMinSalaryChange={setMinSalary}
            maxSalary={maxSalary}
            onMaxSalaryChange={setMaxSalary}
            minGehaltS={minGehaltS}
            onMinGehaltSChange={setMinGehaltS}
            maxGehaltS={maxGehaltS}
            onMaxGehaltSChange={setMaxGehaltS}
            minTagesrate={minTagesrate}
            onMinTagesrateChange={setMinTagesrate}
            maxTagesrate={maxTagesrate}
            onMaxTagesrateChange={setMaxTagesrate}
            selectedMonthYear={selectedMonthYear}
            onMonthYearChange={handleMonthYearChange}
            onLastMonthFilter={handleLastMonthFilter}
            onClearFilters={handleClearFilters}
            resultsCount={filteredAndSortedEmployees?.length}
          />

          {/* Error State */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <span className="text-error font-medium">{error}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                  className="ml-auto"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* No Records for Selected Month/Year */}
          {!isLoading && !error && hasNoRecordsForMonth && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-6 mb-6 text-center">
              <div className="flex flex-col items-center space-y-3">
                <Icon name="Inbox" size={32} className="text-warning" />
                <div>
                  <h3 className="text-lg font-medium text-text-primary mb-1">
                    Keine Einträge für diesen Monat gefunden
                  </h3>
                  <p className="text-text-secondary">
                    Für {new Date(selectedMonthYear.year, selectedMonthYear.month).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })} wurden keine Zeiteinträge gefunden.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
              <h3 className="text-lg font-medium text-text-primary mb-2">Mitarbeiter werden geladen...</h3>
              <p className="text-text-secondary">Bitte warten Sie, während wir die Mitarbeiterdaten abrufen</p>
            </div>
          ) : (
            <EmployeeTable
              employees={filteredAndSortedEmployees}
              onEmployeeSelect={handleEmployeeSelect}
              onEmployeeEdit={handleEmployeeEdit}
              onEmployeeToggle={handleEmployeeToggle}
              onEmployeeDelete={handleEmployeeDelete}
              onPhotoUpload={handlePhotoUpload}
              selectedEmployees={selectedEmployees}
              onSelectionChange={setSelectedEmployees}
              sortConfig={sortConfig}
              onSort={handleSort}
              isColumnsHidden={isColumnsHidden}
            />
          )}

          {/* Bulk Actions Bar */}
          <BulkActionsBar
            selectedCount={selectedEmployees?.length}
            onBulkStatusChange={handleBulkStatusChange}
            onBulkExport={handleBulkExport}
            onBulkDelete={handleBulkDelete}
            onClearSelection={() => setSelectedEmployees([])}
            onDownloadArbeitszeit={handleDownloadArbeitszeit}
            onDownloadArbeitszeitOfficial={handleDownloadArbeitszeitOfficial}
          />

          {/* Employee Modal */}
          <EmployeeModal
            employee={employeeModal?.employee}
            isOpen={employeeModal?.isOpen}
            onClose={() => setEmployeeModal({ isOpen: false, employee: null, mode: 'view' })}
            onSave={handleEmployeeSave}
            mode={employeeModal?.mode}
          />

          {/* Photo Upload Modal */}
          <PhotoUploadModal
            employee={photoModal?.employee}
            isOpen={photoModal?.isOpen}
            onClose={() => setPhotoModal({ isOpen: false, employee: null })}
            onSave={handlePhotoSave}
          />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeManagement;